import { useState, useCallback, lazy, Suspense } from 'react';
import './App.css';
import FileUpload from './components/FileUpload';
import Header from './components/Header';
import Login from './components/Login';
import WordListManager from './components/WordListManager';
import { useAuth } from './hooks/useAuth';
import { useTokenUsage } from './hooks/useTokenUsage';
import { useWordLists } from './hooks/useWordLists';
import { analyzeText } from './services/api';
import { getCachedAnalysis, setCachedAnalysis } from './utils/analysisCache';
import type { TextAnalysisParams, VocabWord, Metadata, WordList, ArtifactResult } from './types';

// Lazy load step components - only loaded when user reaches that step
const VocabSelector = lazy(
  () => import(/* webpackChunkName: "vocab-selector" */ './components/VocabSelector')
);
const OutputSelector = lazy(
  () => import(/* webpackChunkName: "output-selector" */ './components/OutputSelector')
);
const ArtifactDisplay = lazy(
  () => import(/* webpackChunkName: "artifact-display" */ './components/ArtifactDisplay')
);

// App flow steps
// upload → analyzing → select → format → artifact
// Sidebar: saved word lists can jump to "format" step directly

export default function App() {
  const { user, loading: authLoading, isAuthenticated, login, logout } = useAuth();
  const { allLists, saveList, deleteList, updateTitle } = useWordLists();
  const { usage, addUsage, resetSession, contextPercentage } = useTokenUsage();

  const [step, setStep] = useState<string>('upload');
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [analyzeProgress, setAnalyzeProgress] = useState<string>('');
  const [usedCache, setUsedCache] = useState<boolean>(false);

  // Data flowing through the pipeline
  const [currentMetadata, setCurrentMetadata] = useState<Metadata | null>(null);
  const [suggestedWords, setSuggestedWords] = useState<VocabWord[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectedWords, setSelectedWords] = useState<VocabWord[]>([]);
  const [artifact, setArtifact] = useState<ArtifactResult | null>(null);

  // ─── Text ready from FileUpload ──────────────────────────────────────────────
  const handleTextReady = useCallback(
    async (params: TextAnalysisParams) => {
      const { text, title, author, gradeLevel, subject, wordCount } = params;
      setCurrentMetadata({ title, author, gradeLevel, subject });
      setAnalyzing(true);
      setAnalyzeError(null);
      setUsedCache(false);
      setStep('analyzing');

      try {
        // Check cache first
        const cached = getCachedAnalysis(text, gradeLevel, subject, wordCount);

        let words: VocabWord[];

        if (cached) {
          // Use cached result - no API call needed!
          setAnalyzeProgress('Loading from cache...');
          words = cached.words;
          setUsedCache(true);

          // Small delay for UX (so user sees the "analyzing" screen briefly)
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          // No cache - call API
          const result = await analyzeText(
            { text, title, author, gradeLevel, subject, wordCount },
            (progress) => {
              if (progress.status === 'started') {
                setAnalyzeProgress('Starting analysis...');
              } else if (progress.status === 'processing') {
                const elapsedSeconds = (progress.attempt || 0) * 5; // 5 seconds per poll
                const minutes = Math.floor(elapsedSeconds / 60);
                const seconds = elapsedSeconds % 60;
                setAnalyzeProgress(`Processing... (${minutes}m ${seconds}s)`);
              } else if (progress.status === 'completed') {
                setAnalyzeProgress('Complete!');
              }
            }
          );

          // Track token usage from analysis
          if (result.usage) {
            addUsage(result.usage);
          }

          words = result.words;

          // Cache the result for future use
          setCachedAnalysis(
            text,
            gradeLevel,
            subject,
            wordCount,
            words,
            { title, author, gradeLevel, subject, wordCount },
            text.length
          );
        }

        setSuggestedWords(words);
        // Pre-select only grade-level words (primary teaching focus)
        const defaultSelected = new Set(
          words
            .map((w, i) => ({ w, i }))
            .filter(({ w }) => w.difficulty === 'grade-level')
            .map(({ i }) => i)
        );
        setSelectedIds(defaultSelected);

        // Auto-save the complete word list for history
        saveList({
          title,
          author,
          gradeLevel,
          subject,
          words,
        });

        setStep('select');
      } catch (err) {
        setAnalyzeError((err as Error).message);
        setStep('upload');
      } finally {
        setAnalyzing(false);
      }
    },
    [addUsage, saveList]
  );

  // ─── Save word list ───────────────────────────────────────────────────────────
  const handleSaveList = useCallback(
    async (words: VocabWord[]) => {
      saveList({ ...currentMetadata, words } as WordList);
    },
    [currentMetadata, saveList]
  );

  // ─── Proceed to format selection ─────────────────────────────────────────────
  const handleProceedToFormat = useCallback((words: VocabWord[]) => {
    setSelectedWords(words);
    setStep('format');
  }, []);

  // ─── Load a saved list directly to format step ───────────────────────────────
  const handleLoadSavedList = useCallback((list: WordList) => {
    setCurrentMetadata({
      title: list.title,
      author: list.author,
      gradeLevel: list.gradeLevel,
      subject: list.subject,
    });
    setSelectedWords(list.words);
    setStep('format');
  }, []);

  // ─── Artifact generated ───────────────────────────────────────────────────────
  const handleArtifactReady = useCallback(
    (artifactData: ArtifactResult) => {
      // Track token usage from artifact generation
      if (artifactData.usage) {
        addUsage(artifactData.usage);
      }

      setArtifact(artifactData);
      setStep('artifact');
    },
    [addUsage]
  );

  // ─── Start over ───────────────────────────────────────────────────────────────
  const handleStartOver = useCallback(() => {
    setStep('upload');
    setSuggestedWords([]);
    setSelectedIds(new Set());
    setSelectedWords([]);
    setArtifact(null);
    setCurrentMetadata(null);
    setAnalyzeError(null);
  }, []);

  // ─── Loading state ────────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  // ─── Not authenticated ────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return <Login onLogin={login} />;
  }

  // Note: API key setup is no longer needed - site uses centralized CLAUDE_API_KEY

  // ─── Analyzing state (full-screen) ───────────────────────────────────────────
  if (step === 'analyzing') {
    return (
      <>
        <Header
          user={user}
          onLogout={logout}
          step="analyzing"
          onGoHome={handleStartOver}
          tokenUsage={usage}
          contextPercentage={contextPercentage}
          onResetUsage={resetSession}
        />
        <main className="main-content">
          <div className="analyzing-screen">
            <div className="spinner spinner-lg" aria-label="Analyzing text" />
            <h2>Analyzing your text...</h2>
            {analyzeProgress && <p className="progress-message">{analyzeProgress}</p>}
            <p>Identifying vocabulary words, Latin/Greek roots, and difficulty levels.</p>
          </div>
        </main>
      </>
    );
  }

  // ─── Main application ─────────────────────────────────────────────────────────
  return (
    <>
      <Header
        user={user}
        onLogout={logout}
        step={step}
        onGoHome={handleStartOver}
        tokenUsage={usage}
        contextPercentage={contextPercentage}
        onResetUsage={resetSession}
      />
      <main className="main-content">
        <div className="app-layout">
          {/* Sidebar — word lists */}
          {step !== 'artifact' && allLists.length > 0 && (
            <aside className="sidebar">
              <WordListManager
                lists={allLists}
                onLoad={handleLoadSavedList}
                onDelete={deleteList}
                onUpdateTitle={updateTitle}
              />
            </aside>
          )}

          {/* Main panel */}
          <div className={`main-panel ${step === 'artifact' ? 'main-panel-full' : ''}`}>
            {analyzeError && (
              <div className="alert alert-error" role="alert">
                {analyzeError}
                <button className="alert-dismiss" onClick={() => setAnalyzeError(null)}>
                  ✕
                </button>
              </div>
            )}

            {step === 'upload' && (
              <FileUpload onTextReady={handleTextReady} analyzing={analyzing} />
            )}

            {step === 'select' && (
              <Suspense fallback={<div className="spinner" aria-label="Loading" />}>
                <VocabSelector
                  words={suggestedWords}
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  metadata={currentMetadata}
                  onSave={handleSaveList}
                  onProceed={handleProceedToFormat}
                  fromCache={usedCache}
                />
              </Suspense>
            )}

            {step === 'format' && (
              <Suspense fallback={<div className="spinner" aria-label="Loading" />}>
                <OutputSelector
                  words={selectedWords}
                  metadata={currentMetadata}
                  onArtifactReady={handleArtifactReady}
                  onBack={() => setStep('select')}
                />
              </Suspense>
            )}

            {step === 'artifact' && artifact && (
              <Suspense fallback={<div className="spinner" aria-label="Loading" />}>
                <ArtifactDisplay
                  artifact={artifact}
                  metadata={currentMetadata}
                  onBack={() => setStep('format')}
                  onStartOver={handleStartOver}
                />
              </Suspense>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
